// utils/api.ts
import type { FormData as JobFormData, Deponent } from '../types';
import DiagnosticService from '../services/DiagnosticService';
import { devLog } from './devTools';

const API_URL = window.APP_CONFIG?.apiUrl || window.location.origin;
devLog("[DEBUG] API_URL is:", API_URL);

// Main API utility with methods for different request types
export const api = {
  // GET request
  get: async <T = unknown>(endpoint: string, params?: Record<string, string>): Promise<T> => {
    try {
      const url = new URL(`${API_URL}${endpoint}`);
      
      // Add query parameters if they exist
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          url.searchParams.append(key, value);
        });
      }
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include', // Include cookies for session-based auth
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API GET Error:', error);
      
      // Log the error to our diagnostics endpoint
      DiagnosticService.logNetworkError(endpoint, error, { 
        method: 'GET',
        params: params ? JSON.stringify(params) : undefined
      });
      
      throw error;
    }
  },
  
  // POST request
  post: async <T = unknown>(endpoint: string, body?: unknown): Promise<T> => {
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(body),
        credentials: 'include', // Include cookies for session-based auth
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API POST Error:', error);
      
      // Log the error to our diagnostics endpoint
      DiagnosticService.logNetworkError(endpoint, error, {
        method: 'POST',
        // Don't log full body for security, just log whether a body was provided
        bodyProvided: !!body
      });
      
      throw error;
    }
  },
  
  // DELETE request
  delete: async <T = unknown>(endpoint: string, body?: unknown): Promise<T> => {
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'include', // Include cookies for session-based auth
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API DELETE Error:', error);

      // Log the error to our diagnostics endpoint
      DiagnosticService.logNetworkError(endpoint, error, {
        method: 'DELETE',
        bodyProvided: !!body
      });

      throw error;
    }
  },

  // PATCH request
  patch: async <T = unknown>(endpoint: string, body?: unknown): Promise<T> => {
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(body),
        credentials: 'include', // Include cookies for session-based auth
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API PATCH Error:', error);

      // Log the error to our diagnostics endpoint
      DiagnosticService.logNetworkError(endpoint, error, {
        method: 'PATCH',
        bodyProvided: !!body
      });

      throw error;
    }
  },

  // File upload - special case for multipart form data
  uploadFile: async <T = unknown>(endpoint: string, file: File, additionalData?: Record<string, string>): Promise<T> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Add any additional data fields
      if (additionalData) {
        Object.entries(additionalData).forEach(([key, value]) => {
          formData.append(key, value);
        });
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        body: formData,
        credentials: 'include', // Include cookies for session-based auth
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API Upload Error:', error);
      
      // Log the error to our diagnostics endpoint
      DiagnosticService.logNetworkError(endpoint, error, {
        method: 'POST (File Upload)',
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });
      
      throw error;
    }
  }
};

/**
 * Validate a token by checking if it exists in the system
 * @param {string} token - The token to validate (6-digit alphanumeric)
 * @returns {Promise<boolean>} True if token is valid
 */
export const validateToken = async (token: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_URL}/api/jobs/prefetch?token=${token}`);
    return response.status === 200; // 200 means token exists, 404 means it doesn't
  } catch (error) {
    console.error('Error validating token:', error);
    return false;
  }
};

/**
 * Verify a PIN with a token by attempting to fetch full job data
 * @param {string} token - The token (6-digit alphanumeric)
 * @param {string} pin - The PIN to verify
 * @returns {Promise<boolean>} True if PIN is valid
 */
export const verifyPin = async (token: string, pin: string): Promise<boolean> => {
  try {
    // We validate the PIN by trying to fetch the full job data, which requires a valid PIN
    const response = await fetch(`${API_URL}/api/jobs/fetch?token=${token}&pin=${pin}`);
    
    // Check if we got a 200 status code
    if (response.status === 200) {
      return true;
    }
    
    // Non-200 response (401 means it's not valid)
    return false;
  } catch (error) {
    console.error('Error verifying PIN:', error);
    return false;
  }
};

/**
 * Fetch job data for a token (from prefetch endpoint - limited data)
 * @param {string} token - The token
 * @returns {Promise<Object>} The job data
 */
export const fetchJobData = async (token: string): Promise<Partial<JobFormData>> => {
  try {
    const response = await fetch(`${API_URL}/api/jobs/prefetch?token=${token}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch job data');
    }
    
    // The prefetch endpoint returns the job data directly
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching job data:', error);
    throw error;
  }
};

/**
 * Fetch full job data including reporter information
 * @param {string} token - The token
 * @param {string} pin - The PIN to verify
 * @returns {Promise<Object>} The complete job data
 */
export const fetchFullJobData = async (token: string, pin: string): Promise<Partial<JobFormData>> => {
  try {
    const response = await fetch(`${API_URL}/api/jobs/fetch?token=${token}&pin=${pin}`);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Invalid PIN');
      }
      throw new Error('Failed to fetch job data');
    }
    
    // The fetch endpoint returns the complete job data
    const data = await response.json();

    // Create a modified version of the data that includes properly formatted reporterName
    const modifiedData = { ...data };
    
    // Extract reporter name from reporters array if available
    if (data.reporters && Array.isArray(data.reporters) && data.reporters.length > 0) {
      // Find the reporter matching the logged-in reporter's resource email
      const matchingReporter = data.reporters.find((rep: { pin?: string; name?: string }) => rep.pin === pin) || data.reporters[0];
      let reporterName = matchingReporter.name || '';
      
      // Format name if needed
      if (reporterName && reporterName.includes(' - ')) {
        reporterName = reporterName.split(' - ')[0];
      }
      
      // Add the formatted reporter name to the data
      modifiedData.reporterName = reporterName;
    }
    
    // Just pass through the deponents and attorneys arrays
    // The actual processing is done in App.tsx to ensure proper structure
    // We're only receiving basic data from the server:
    // - Deponents: just name
    // - Attorneys: name, email, firm, and possibly mailingAddress
    
    return modifiedData;
  } catch (error) {
    console.error('Error fetching full job data:', error);
    throw error;
  }
};

interface FetchJobByNumberResponse {
  success: boolean;
  message?: string;
  job: Omit<Partial<JobFormData>, 'deponents'> & {
    deponents?: Partial<Deponent>[];
    witnessName?: string;
    deponentName?: string;
  };
}

/**
 * Fetch job by number for authenticated users
 * @param {string} jobNumber - The job number
 * @returns {Promise<Partial<JobFormData>>} The job data
 */
export const fetchJobByNumber = async (jobNumber: string): Promise<Partial<JobFormData>> => {
  try {
    const response = await api.get<FetchJobByNumberResponse>(`/api/jobs/fetch/${jobNumber}`);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch job data');
    }
    
    // Make sure we have at least one deponent, even if with an empty name
    if (!response.job.deponents || !Array.isArray(response.job.deponents) || response.job.deponents.length === 0) {
      const deponentName = response.job.witnessName || response.job.deponentName || '';
      response.job.deponents = [{ name: deponentName }];
    }
    
    // Server returns partial deponents (name only); App.tsx fills in required fields
    return response.job as Partial<JobFormData>;
  } catch (error) {
    console.error('Error fetching job by number:', error);
    throw error;
  }
};

interface UploadFilesResult {
  success: boolean;
  files?: File[];
  message?: string;
}

/**
 * Upload files for a token
 * @param {string} token - The token
 * @param {string} fileType - The type of file ('transcriptFiles', 'backupFiles', 'exhibitFiles', 'audioFiles', or 'invoiceFile')
 * @param {FileList} files - The files to upload
 * @returns {Promise<Object>} Upload response
 */
export const uploadFiles = async (
  token: string, 
  fileType: 'transcriptFiles' | 'backupFiles' | 'exhibitFiles' | 'audioFiles' | 'invoiceFile', 
  files: FileList
): Promise<UploadFilesResult> => {
  try {
    // We need to upload one file at a time as our server endpoint expects
    const uploadResults = [];
    
    for (let i = 0; i < files.length; i++) {
      const formData = new FormData();
      formData.append('fileType', fileType);
      formData.append('files', files[i]); // Server expects 'files' not 'file'
      
      const response = await fetch(`${API_URL}/api/uploads/${token}?fileType=${encodeURIComponent(fileType)}`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Failed to upload file ${files[i].name}`);
      }
      
      const result = await response.json();
      uploadResults.push(result);
    }
    
    return {
      success: true,
      message: `Successfully uploaded ${files.length} files`
    };
  } catch (error) {
    console.error('Error uploading files:', error);
    throw error;
  }
};

interface SubmitFormResult {
  success: boolean;
  message?: string;
  confirmationId?: string;
  error?: string;
}

/**
 * Submit the form data
 * @param {Object} formData - The form data to submit
 * @returns {Promise<Object>} Submission response
 */
export const submitForm = async (formData: JobFormData): Promise<SubmitFormResult> => {
  try {
    const response = await fetch(`${API_URL}/api/jobs/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    });
    
    if (!response.ok) {
      throw new Error('Failed to submit form');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error submitting form:', error);
    throw error;
  }
};

interface CreateLinkResult {
  success: boolean;
  token?: string;
  formUrl?: string;
  message?: string;
}

/**
 * Create a link for a new reporter worksheet
 * @param {Object} jobData - The job data
 * @returns {Promise<Object>} Link creation response
 */
export const createWorksheetLink = async (jobData: Partial<JobFormData>): Promise<CreateLinkResult> => {
  try {
    const response = await fetch(`${API_URL}/api/jobs/create-link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(jobData)
    });

    if (!response.ok) {
      throw new Error('Failed to create worksheet link');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating worksheet link:', error);
    throw error;
  }
};

// Draft worksheet API functions

export interface FileMetadata {
  filename: string;
  originalName: string;
  storagePath: string;
  size: number;
  mimetype: string;
  jobNumber?: string;
  deponentId?: string;
  containerName?: string;
}

export interface FileReferences {
  deponents?: Record<string, {
    transcriptFiles?: FileMetadata[];
    exhibitFiles?: FileMetadata[];
    audioFiles?: FileMetadata[];
  }>;
  attorneys?: Record<string, {
    orders?: Record<number, {
      deponentId: string;
      orderSubmissionFiles: FileMetadata[];
    }>;
  }>;
  invoiceFiles?: FileMetadata[];
}

interface SaveDraftResult {
  success: boolean;
  message?: string;
  id?: number;
  error?: string;
}

/**
 * Save a draft worksheet
 * @param {string} jobNumber - The job number
 * @param {Object} formData - The form data to save
 * @param {number} currentStep - The current step in the workflow
 * @param {string} token - Optional token
 * @param {Object} fileReferences - Optional file references for already-uploaded files
 * @returns {Promise<SaveDraftResult>} Save draft response
 */
export const saveDraft = async (
  jobNumber: string,
  formData: JobFormData,
  currentStep: number,
  token?: string,
  fileReferences?: FileReferences
): Promise<SaveDraftResult> => {
  try {
    const response = await fetch(`${API_URL}/api/jobs/draft/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include', // Include session cookie for authentication
      body: JSON.stringify({
        jobNumber,
        token,
        currentStep,
        formData,
        fileReferences
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to save draft');
    }

    return await response.json();
  } catch (error) {
    console.error('Error saving draft:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

interface LoadDraftResult {
  success: boolean;
  draft?: {
    id: number;
    jobNumber: string;
    token?: string;
    reporterId: string;
    currentStep: number;
    formData: JobFormData;
    fileReferences?: FileReferences;
    lastSavedAt: string;
    createdAt: string;
    updatedAt: string;
  };
  error?: string;
}

/**
 * Load a draft worksheet
 * @param {string} jobNumber - The job number
 * @returns {Promise<LoadDraftResult>} Load draft response
 */
export const loadDraft = async (
  jobNumber: string
): Promise<LoadDraftResult> => {
  try {
    const response = await fetch(
      `${API_URL}/api/jobs/draft/${jobNumber}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include' // Include session cookie for authentication
      }
    );

    if (response.status === 404) {
      // No draft found is not an error
      return {
        success: false,
        error: 'No draft found'
      };
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to load draft');
    }

    return await response.json();
  } catch (error) {
    console.error('Error loading draft:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

interface DeleteDraftResult {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Delete a draft worksheet
 * @param {string} jobNumber - The job number
 * @returns {Promise<DeleteDraftResult>} Delete draft response
 */
export const deleteDraft = async (
  jobNumber: string
): Promise<DeleteDraftResult> => {
  try {
    const response = await fetch(
      `${API_URL}/api/jobs/draft/${jobNumber}`,
      {
        method: 'DELETE',
        credentials: 'include' // Include session cookie for authentication
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete draft');
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting draft:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};